import { IVeriTableHeader, IVeriTableRow } from "@directives/_veri/veriTable/veriTable.component";
import { IGeofence } from "@interfaces/IGeofences";
import { GeofenceService } from "@services/geofence.service";
import * as angular from "angular";
import "../csvImporter.html";
import "../csvImporter.scss";
import { FieldType } from "../enums/FieldType";
import { ImportSource } from "../enums/ImportSource";
import { IImportConfig } from "../interfaces/IImportConfig";
import { IImportCount } from "../interfaces/IImportCount";
import { IImportGeofence } from "../interfaces/IImportGeofence";
import { IImportOption } from "../interfaces/IImportOption";
import { IImportValidationStatus } from "../interfaces/IImportValidation";
import { GeofenceImporterService } from "./geofenceImporter.service";

export class GeofenceImporterController {
    loading = false;
    importConfig: IImportConfig;

    geofences: IGeofence[];

    static $inject = ["$mdDialog", "geofenceService", "geofenceImporterService"];

    constructor(
        private readonly $mdDialog: angular.material.IDialogService,
        private readonly geofenceService: GeofenceService,
        private readonly geofenceImporterService: GeofenceImporterService
    ) {
        this.importConfig = {
            importOptions: this.getImportOptions(),
            tableHeaders: this.getTableHeaders(),
            informationText: this.getInformationText(),
            informationTableHeader: this.getInformationTableHeader(),
            informationTableRow: this.getInformationTableRow(),
            importSource: ImportSource.Geofence
        };
    }

    $onInit() {
        this.loading = true;

        this.geofenceService.getGeofences().then((geofences) => {
            this.geofences = geofences;
                this.loading = false;
            });
    }

    hide(): void {
        this.$mdDialog.hide();
    }

    validateRow(csvLine: string, importOptions: IImportOption[], rowIdx: number): IImportValidationStatus {
        return this.geofenceImporterService.validateRow(csvLine, importOptions, this.geofences, rowIdx);
    }

    validateData(csvLines: string[], importOptions: IImportOption[]): angular.IPromise<IVeriTableRow<IImportGeofence>[]> {
        return this.geofenceImporterService.validateData(csvLines, importOptions, this.geofences)
            .then((importResult) => {
                return importResult;
            });
    }

    submit(importData: any[]): angular.IPromise<IImportCount> {
        this.loading = true;

        const geofences = this.getGeofencesFromImportData(importData as IImportGeofence[]);

        return this.geofenceService.importGeofences(geofences)
            .then((importResult) => {
                this.loading = false;

                return {
                    successCount: importResult.successCount,
                    failedCount: importResult.failedCount
                };
        });
    }

    private getGeofencesFromImportData(importGeofences: IImportGeofence[]): IGeofence[] {
        const geofences = [] as IGeofence[];
        importGeofences.forEach((importGeofence) => {
            geofences.push(importGeofence.geofence);
        });

        return geofences;
    }

    private validateLongitude(valText: string): boolean {
        const lngVal = +valText;

        return lngVal >= -180 && lngVal <= 180;
    }

    private validateLatitude(valText: string): boolean {
        const latVal = +valText;

        return latVal >= -90 && latVal <= 90;
    }

    private validateRadius(valText: string): boolean {
        const minRadius = 50;
        const maxRadius = 1000;

        const radiusVal = +valText;

        return radiusVal >= minRadius && radiusVal <= maxRadius;
    }

    private getTableHeaders(): IVeriTableHeader[] {
        return [
            { title: "Line No.", hide: true },
            { title: "Name", sortable: true, width: 160 },
            { title: "Ext Description", sortable: true, width: 160 },
            { title: "Address 1", sortable: false, width: 190 },
            { title: "Post Code and Address 4", sortable: false, width: 190 },
            { title: "Latitude", sortable: false, width: 100 },
            { title: "Longitude", sortable: false, width: 100 }
        ];
    }

    private getImportOptions(): IImportOption[] {

        const csvImportOptions: IImportOption[] = [{
                columnName: "name",
                aliases: ["geofence", "geofenceName", "description"],
                displayName: "Geofence Name",
                fieldType: FieldType.String,
                indexPosition: 0,
                isRequired: true
            },
            {
                columnName: "extDescription",
                aliases: ["externalDescription", "externalReference", "externalRef", "extRef", "extReference"],
                displayName: "External Reference",
                fieldType: FieldType.String,
                indexPosition: 1,
                isRequired: false
            },
            {
                columnName: "line1",
                aliases: ["add1", "addr1", "address1", "addressLine1"],
                displayName: "Line 1",
                fieldType: FieldType.String,
                indexPosition: 2,
                isRequired: false
            },
            {
                columnName: "line2",
                aliases: ["add2", "addr2", "address2", "addressLine2"],
                displayName: "Line 2",
                fieldType: FieldType.String,
                indexPosition: 3,
                isRequired: false
            },
            {
                columnName: "line3",
                aliases: ["add3", "addr3", "address3", "addressLine3"],
                displayName: "Line 3",
                fieldType: FieldType.String,
                indexPosition: 4,
                isRequired: false
            },
            {
                columnName: "line4",
                aliases: ["add4", "addr4", "address4", "addressLine4"],
                displayName: "Line 4",
                fieldType: FieldType.String,
                indexPosition: 5,
                isRequired: false
            },
            {
                columnName: "postcode",
                aliases: ["postal code"],
                displayName: "Line 4",
                fieldType: FieldType.String,
                indexPosition: 6,
                isRequired: false
            },
            {
                columnName: "latitude",
                aliases: ["lat"],
                displayName: "Latitude",
                fieldType: FieldType.Double,
                indexPosition: 7,
                isRequired: true,
                validationText: "Value must be between -90 and 90",
                validate: (val) => { return this.validateLatitude(val); }
            },
            {
                columnName: "longitude",
                aliases: ["long", "lng"],
                displayName: "Longitude",
                fieldType: FieldType.Double,
                indexPosition: 8,
                isRequired: true,
                validationText: "Value must be between -180 and 180",
                validate: (val) => { return this.validateLongitude(val); }
            },
            {
                columnName: "radius",
                aliases: [],
                displayName: "Radius",
                fieldType: FieldType.Integer,
                indexPosition: 9,
                isRequired: false,
                defaultValue: 50,
                validate: (val) => { return this.validateRadius(val); }
            }
        ];

        return csvImportOptions;
    }

    private getInformationText(): string {
        return "Import format: Geofence name, External Ref, Address 1, Address 2, Address 3, Address 4, Postcode, Latitude, Longitude, Radius <br /><br />" +
            "Note: The import CSV file can contain an optional title row.";
    }

    // these need testing - DEBUG
    private getInformationTableHeader(): string {
        return "Name,Ext.Ref,Line 1,Line 2,Line 3,Line 4,Postcode,Latitude,Longitude,Radius";
    }

    private getInformationTableRow(): string {
        return "Andover,And001,1 Andover Street,,,Andover,AN12AB,51.20277,-5.86476,100";
    }
}

export class GeofenceImporterComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.controller = GeofenceImporterController;
        this.template = require("./geofenceImporter.dialog.html");
    }
}
